import { useState, useEffect } from 'react'
import { featureToggles } from '../services/featureToggles'
import './FeatureTogglePanel.css'

/**
 * Feature Toggle Panel
 * 
 * Development-only component for managing feature toggles.
 * Only visible in development mode.
 */
export function FeatureTogglePanel() {
  const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'
  const [toggles, setToggles] = useState(featureToggles.getAll())
  const [isOpen, setIsOpen] = useState(false)
  const [productMode, setProductMode] = useState<'auto' | 'pms' | 'ppt'>('auto')

  useEffect(() => {
    setToggles(featureToggles.getAll())
    const stored = localStorage.getItem('clnicl_dev_product_type_override')
    if (stored === 'ppt' || stored === 'pms') {
      setProductMode(stored)
    } else {
      setProductMode('auto')
    }
  }, [])

  if (!isDevelopment) {
    return null
  }

  const handleToggle = (key: keyof typeof toggles, value: boolean) => {
    featureToggles.set(key, value)
    setToggles({ ...toggles, [key]: value })
  }

  const handleReset = () => {
    if (confirm('Reset all feature toggles to defaults?')) {
      featureToggles.reset()
    }
  }

  const handleProductModeChange = (value: 'auto' | 'pms' | 'ppt') => {
    if (value === 'auto') {
      localStorage.removeItem('clnicl_dev_product_type_override')
    } else {
      localStorage.setItem('clnicl_dev_product_type_override', value)
    }
    setProductMode(value)
    window.location.reload()
  }

  return (
    <>
      <button
        className="feature-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Feature Toggles"
      >
        ⚙️
      </button>
      {isOpen && (
        <div className="feature-toggle-panel">
          <div className="feature-toggle-header">
            <h3>Feature Toggles</h3>
            <button onClick={() => setIsOpen(false)}>×</button>
          </div>
          <div className="feature-toggle-content">
            <div className="feature-toggle-item">
              <label>
                <input
                  type="checkbox"
                  checked={toggles.rootDomainRegistration}
                  onChange={(e) => handleToggle('rootDomainRegistration', e.target.checked)}
                />
                <span>Root Domain Registration</span>
              </label>
              <p className="feature-toggle-description">
                Show registration page on root domain (clnicl.ie)
              </p>
            </div>
            <div className="feature-toggle-item">
              <label>
                <input
                  type="checkbox"
                  checked={toggles.multiTenancy}
                  onChange={(e) => handleToggle('multiTenancy', e.target.checked)}
                />
                <span>Multi-Tenancy</span>
              </label>
              <p className="feature-toggle-description">
                Enable multi-tenant routing (subdomain-based)
              </p>
            </div>
            <div className="feature-toggle-item">
              <label>
                <input
                  type="checkbox"
                  checked={toggles.appDevRoutes}
                  onChange={(e) => handleToggle('appDevRoutes', e.target.checked)}
                />
                <span>App Dev Routes</span>
              </label>
              <p className="feature-toggle-description">
                Enable /app/* routes for accessing main app on root domain in dev
              </p>
            </div>
            <div className="feature-toggle-item">
              <label htmlFor="dev-product-mode">
                <span>Product Mode (dev only)</span>
              </label>
              <select
                id="dev-product-mode"
                value={productMode}
                onChange={(e) => handleProductModeChange(e.target.value as 'auto' | 'pms' | 'ppt')}
              >
                <option value="auto">Auto (from tenant setting)</option>
                <option value="pms">Force PMS</option>
                <option value="ppt">Force PPT</option>
              </select>
              <p className="feature-toggle-description">
                Applies local dev override for PPT/PMS experience.
              </p>
            </div>
          </div>
          <div className="feature-toggle-footer">
            <button onClick={handleReset} className="btn-reset">
              Reset to Defaults
            </button>
            <p className="feature-toggle-hint">
              Changes require page reload. Use console: <code>window.featureToggles</code>
            </p>
          </div>
        </div>
      )}
    </>
  )
}






























